import { Link, useLocation } from "react-router";
import classNames from "classnames";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import cabologo from "~/assets/logos/logo.svg";

type NavItem = {
  label: string;
  to: string;
  id: string;
};

export function NavBar({
  activeSection,
  title,
  logo,
  logoTo,
  navItems,
  right,
  sticky = false,
}: {
  activeSection?: string;
  title?: string;
  logo?: React.ReactNode | string;
  logoTo: string;
  navItems: NavItem[];
  right?: React.ReactNode;
  sticky?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const mobileMenuId = "public-main-menu";

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // 100px offset
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setOpen(false);
    }
  };
  const location = useLocation();

  const Button = ({ children, className, item }: { children: React.ReactNode; className: string; item: NavItem }) => {
    const [path, id] = item.to?.split("#") ?? [];
    const isTheSamePage = path === location.pathname;
    const ariaCurrent = activeSection === item.id ? ("location" as const) : undefined;

    if (id && isTheSamePage) {
      return (
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            return scrollToSection(id);
          }}
          className={className}
          aria-current={ariaCurrent}
        >
          {children}
        </button>
      );
    }

    return (
      <Link to={item.to} className={className} onClick={() => setOpen(false)} aria-current={ariaCurrent}>
        {children}
      </Link>
    );
  };

  return (
    <>
      <nav aria-label={t("Mobile Hauptnavigation")} aria-hidden={!open}>
        <ul
          id={mobileMenuId}
          className={classNames(
            "fixed inset-x-4 top-24 z-40 flex flex-col items-stretch gap-2 rounded-2xl border border-white/70 bg-white/95 p-4 shadow-lg shadow-slate-900/10 backdrop-blur-md lg:hidden",
            {
              hidden: !open,
            },
          )}
        >
          {navItems.map((navItem) => {
            const id = navItem.id;
            return (
              <li key={navItem.to} className="grid">
                <Button
                  item={navItem}
                  className={classNames(
                    "relative block rounded-xl px-4 py-4 text-center text-sm font-semibold transition-all duration-300 ease-in-out",
                    "group focus:outline-none",
                    {
                      "text-gray-500": activeSection !== id,
                    },
                  )}
                >
                  <span className="relative z-10">{navItem.label}</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      "absolute inset-0 rounded-xl bg-gray-100 transition-all duration-300 ease-in-out",
                      "group-hover:scale-100 group-hover:opacity-100",
                      {
                        "scale-50 opacity-0": activeSection !== id,
                        "scale-100 opacity-100": activeSection === id,
                      },
                    )}
                  ></span>
                </Button>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className={classNames("z-50 pt-3", sticky ? "sticky top-0" : "relative")}>
        <div className="public-container flex-row items-center justify-between gap-4 rounded-2xl border border-white/70 bg-white/75 px-4 py-3 shadow-lg shadow-slate-900/10 backdrop-blur-md sm:px-5">
          <Link
            to={logoTo}
            className="flex min-w-0 items-center gap-3"
            aria-label={title ? `${title} ${t("Startseite")}` : undefined}
          >
            {typeof logo === "string" || logo == null ? (
              <img src={logo ?? cabologo} alt={title ? "" : "CaboCheck"} className="h-8 w-auto max-w-52 sm:h-9" />
            ) : (
              logo
            )}

            {title ? <span className="truncate text-sm font-semibold text-slate-950 sm:text-base">{title}</span> : null}
          </Link>

          <div className="flex items-center justify-end gap-2 sm:gap-5">
            <div className="lg:hidden">{right}</div>
            <button
              type="button"
              className={classNames("lg:hidden", {
                hidden: navItems.length === 0,
              })}
              onClick={() => setOpen(!open)}
              aria-controls={mobileMenuId}
              aria-expanded={open}
              aria-label={open ? t("Menü schließen") : t("Menü öffnen")}
            >
              <Menu aria-hidden="true" className="h-8 w-8 text-blue-550" />
            </button>
            <nav aria-label={t("Hauptnavigation")} className="hidden lg:block">
              <ul className="flex items-center justify-center gap-2 text-center text-sm font-semibold">
                {navItems.map((navItem) => {
                  const id = navItem.id;
                  return (
                    <li key={navItem.to}>
                      <Button
                        item={navItem}
                        className={classNames(
                          "relative block rounded-xl px-4 py-2.5 transition-all duration-300 ease-in-out",
                          "group focus:outline-none",
                          { "text-gray-500": activeSection !== id },
                        )}
                      >
                        <span className="relative z-10">{navItem.label}</span>
                        <span
                          aria-hidden="true"
                          className={classNames(
                            "absolute inset-0 rounded-xl bg-gray-100 transition-all duration-300 ease-in-out",
                            "group-hover:scale-100 group-hover:opacity-100",
                            {
                              "scale-50 opacity-0": activeSection !== id,
                              "scale-100 opacity-100": activeSection === id,
                            },
                          )}
                        ></span>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div className="hidden lg:block">{right}</div>
          </div>
        </div>
      </div>
    </>
  );
}
